<template>
  <div class="ptz ptz-presets" :class="{ 'ptz-presets--without-presets': !presets.length }" v-if="element">
    <div class="ptz-presets__wrapper">
      <div class="ptz-presets__list">
        <div v-for="preset in presets" :key="preset.id" class="ptz-presets__item" @click="handlePresetClick($event, preset)"
          :class="{ 'ptz-presets__item--selected': selectedPreset?.id === preset.id, 'ptz-presets__item--editing': editingPresetId === preset.id }">
          <div v-if="editingPresetId !== preset.id" class="ptz-presets__item-display">
            <button class="ptz-presets__name" :title="`Load preset: ${preset.name}`">
              {{ preset.name }}
            </button>
            <div class="ptz-presets__item-actions">
              <b-dropdown position="is-top-right">
                <template #trigger>
                  <button class="ptz-presets__item-action ptz-presets__dropdown-trigger">
                    <i class="mdi mdi-dots-vertical"></i>
                  </button>
                </template>
                
                <b-dropdown-item @click="startEditingName(preset)">
                  <i class="mdi mdi-pencil"></i>
                  <span>Edit name</span>
                </b-dropdown-item>
                
                <b-dropdown-item @click="saveCurrentPreset(preset)">
                  <i class="mdi mdi-content-save"></i>
                  <span>Save current position</span>
                </b-dropdown-item>
                
                <b-dropdown-item @click="deletePreset(preset.id)" class="has-text-danger">
                  <i class="mdi mdi-close"></i>
                  <span>Delete preset</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div v-else class="ptz-presets__item-edit">
            <input ref="editInput" v-model="editingName" @keyup.enter="savePresetName(preset)"
              @keyup.escape="cancelEditingName" class="ptz-presets__name-input" maxlength="20" />
            <button class="ptz-presets__save-name-btn" @click.stop="savePresetName(preset)" title="Save name">
              <i class="mdi mdi-check"></i>
            </button>
          </div>
        </div>
        <div class="ptz-presets__item ptz-presets__item--new-preset" v-if="!editingPresetId && presets.length <= 5" @click="saveAsNewPreset">
          <button v-if="presets.length" class="ptz-presets__item-action" :disabled="!videoDeviceId">
            <i class="mdi mdi-plus"></i>
          </button>
          <span v-else class="new-preset-text">
            Add a New Preset
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import to from "@/lib/to"

export default {
  name: "PtzPresets",
  props: ['element', 'videoDeviceId', 'webrtcSession', 'skipLoading'],
  data() {
    return {
      presets: [],
      selectedPreset: null,
      editingPresetId: null,
      editingName: '',
    }
  },
  methods: {
    async fetchPresets() {
      if (!this.videoDeviceId) return

      const [err, response] = await to(this.$http.get(`/ptz_presets/for_device/${this.videoDeviceId}`, { skipDivisions: true, skipLoading: !!this.skipLoading }))
      if (!err) {
        this.presets = response.data
      }
    },

    async saveCurrentPreset(preset) {
      this.saveDevicePreset(preset.preset_index)
      this.$buefy.toast.open({
        message: `Current position saved to "${this.$helpers.escapeHtml(preset.name)}"`,
        type: 'is-success',
        duration: 2000
      })
    },

    saveDevicePreset(index) {
      if (!this.webrtcSession) return;

      this.webrtcSession.sendDeviceDataChannelMessage({
        type: 'ptz',
        action: 'save_preset',
        preset_index: index,
      })
    },

    recallDevicePreset(index) {
      if (!this.webrtcSession) return;

      this.webrtcSession.sendDeviceDataChannelMessage({
        type: 'ptz',
        action: 'recall_preset',
        preset_index: index,
      })
    },

    async saveAsNewPreset() {
      if (!this.videoDeviceId) {
        this.$buefy.toast.open({
          message: 'No video device available',
          type: 'is-danger',
          duration: 2000
        })
        return
      }

      const [err, response] = await to(this.$http.post('/ptz_presets', {
        ptz_preset: {
          name: `Preset ${this.presets.length + 1}`,
          video_device_id: this.videoDeviceId
        }
      }))

      if (!err) {
        const newPreset = response.data
        this.presets.push(newPreset)
        this.selectedPreset = newPreset

        this.saveDevicePreset(newPreset.preset_index)

        this.$buefy.toast.open({
          message: `New preset "${this.$helpers.escapeHtml(newPreset.name)}" saved`,
          type: 'is-success',
          duration: 2000
        })

        this.$nextTick(() => {
          this.startEditingName(newPreset)
        })
      }
    },

    handlePresetClick(event, preset) {
      if (event.target.closest('.ptz-presets__item-actions')) {
        return
      }
      this.selectPreset(preset)
    },

    selectPreset(preset) {
      this.selectedPreset = preset
      this.recallDevicePreset(preset.preset_index)
    },

    startEditingName(preset) {
      this.editingPresetId = preset.id
      this.editingName = preset.name
      this.$nextTick(() => {
        const input = this.$refs.editInput?.[0]
        if (input) {
          input.focus()
          input.select()
        }
      })
    },

    async savePresetName(preset) {
      if (!this.editingName.trim()) {
        this.cancelEditingName()
        return
      }

      const [err] = await to(this.$http.put(`/ptz_presets/${preset.id}`, {
        ptz_preset: {
          name: this.editingName.trim()
        }
      }))

      if (!err) {
        preset.name = this.editingName.trim()
        this.$buefy.toast.open({
          message: `Preset renamed to "${this.$helpers.escapeHtml(preset.name)}"`,
          type: 'is-success',
          duration: 2000
        })
      }

      this.cancelEditingName()
    },

    cancelEditingName() {
      this.editingPresetId = null
      this.editingName = ''
    },

    async deletePreset(presetId) {
      const preset = this.presets.find(p => p.id === presetId)
      if (!preset) return

      this.$buefy.dialog.confirm({
        title: 'Delete Preset',
        message: `Are you sure you want to delete preset "${this.$helpers.escapeHtml(preset.name)}"?`,
        confirmText: 'Delete',
        type: 'is-danger',
        onConfirm: async () => {
          const [err] = await to(this.$http.delete(`/ptz_presets/${presetId}`))

          if (!err) {
            this.presets = this.presets.filter(p => p.id !== presetId)
            if (this.selectedPreset?.id === presetId) {
              this.selectedPreset = null
            }
            this.$buefy.toast.open({
              message: `Preset "${this.$helpers.escapeHtml(preset.name)}" deleted`,
              type: 'is-success',
              duration: 2000
            })
          }
        }
      })
    }
  },

  watch: {
    videoDeviceId: {
      handler(newId) {
        if (newId) {
          this.fetchPresets()
        } else {
          this.presets = []
          this.selectedPreset = null
        }
      },
      immediate: true
    }
  },

  mounted() {
    if (this.videoDeviceId) {
      this.fetchPresets()
    }
  }
}
</script>

<style scoped lang="scss">
.ptz-presets {
  margin-left: 20px;
  display: flex;
  align-items: center;
}

.ptz-presets--without-presets {
  .ptz-presets__actions {
    border-right: none;
    padding-right: 0;
  }
}

.ptz-presets__wrapper {
  display: flex;
  gap: 8px;
}

.ptz-presets__actions {
  display: flex;
  gap: 8px;
  flex-direction: column;
  border-right: 1px solid #ada9ad;
  padding-right: 8px;
}

.ptz-presets__global-action {
  padding: 0 10px;
  height: 40px;
  border: none;
  background: #ffffff7a;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  gap: 5px;

  i {
    font-size: 15px;
  }

  &:hover:not(:disabled) {
    background: #ffffffc7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.ptz-presets__list {
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-auto-flow: column;
  gap: 4px;
}

.ptz-presets__item-actions {
  align-items: center;
  justify-content: center;
  padding-top: 3px;
}
.ptz-presets__item {
  background: #ffffff7a;
  border-radius: 6px;
  display: flex;
  padding: 7px 5px;
  border: 1px solid transparent;
  color: #333;
  cursor: pointer;
  height: 55px;
  min-width: 100px;
  text-align: center;
  &:hover {
    background: #ffffffc7;

    .ptz-presets__item-actions {
      display: flex;
    }
    .ptz-presets__dropdown-trigger {
      background: rgba(79, 195, 247, 0.56) !important;
      color: #fff
    }
  }

  &--selected,
  &--selected:hover {
    background: #4fc3f78f;
    border: 1px solid #7dd7ff;
    color: #fff;
  }

  &--editing {
    padding: 6px 5px;
  }
}

.ptz-presets__item-display {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.ptz-presets__name {
  flex: 1;
  background: none;
  border: none;
  font-size: 13px;
  line-height: 1;
  text-align: left;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  cursor: pointer;
  padding-top: 3px;
}

.ptz-presets__item-action,
.ptz-presets__save-name-btn {
  border: none;
  background: none;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  color: #666;
  background: #ffffffcb;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  margin-left: 5px;
  &:hover {
    background: #ffffff;
    color: #000;
  }
}

.ptz-presets__item-action {
  border-radius: 100%;
  width: 20px;
  height: 20px;
  background: #d3eeff;
}


.ptz-presets__item-action--delete:hover {
  color: #ff4757;
}

.ptz-presets__dropdown-trigger {
  background: transparent !important;
  color: #fff;
  i {
    padding-top: 2px !important;
  }
}

.dropdown-item {
  display: flex !important;
  align-items: center !important;
  gap: 8px;
  
  i {
    width: 16px;
    font-size: 14px;
  }
  
  span {
    font-size: 13px;
  }
}

.ptz-presets__item-edit {
  display: flex;
  align-items: center;
  gap: 2px;
}

.ptz-presets__name-input {
  flex: 1;
  border: none;
  border-radius: 3px;
  font-size: 13px;
  padding: 5px 2px;
  background: white;
  outline: none;

  &:focus {
    border-color: #4fc3f7;
  }
}
.ptz-presets__item--new-preset {
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px dashed #7dd7ff;
  color: #fff;
  .new-preset-text {
    font-size: 13px;
    color: #fff;
  }
  &:hover {
    border: 1px solid #7dd7ff;
    background: #7dd7ff;
    .ptz-presets__item-action {
      color: $main-text-color;
    }
  }
}
</style>
